///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2022
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import ProgressBar from "./summary/ProgressBar";
import Team from "./summary/Team";
import Messages from "./summary/Messages";
import Attachments from "./summary/Attachments";
import ShipperHistory from "./summary/ShipperHistory";
import Assignments from "./summary/Assignments";

import {
  fetchBidSummaryTabDetails,
  fetchBidTeam,
  fetchBidMessages,
  fetchBidAttachments,
  fetchBidShipperHistory,
  fetchTLLaneCountByRegion
} from "../../slices/bidSummarySlice";
import { mode as modeTypes, userRole } from "../../helpers/types";

export const Summary = ({ bidId, role }) => {
  const dispatch = useDispatch();
  const {
    summary,
    summaryLoading,
    summaryError,
    teamError,
    teamLoading,
    team,
    messagesError,
    messagesLoading,
    messages,
    shipperHistoryError,
    shipperHistoryLoading,
    shipperHistory,
    attachmentsError,
    attachmentsLoading,
    attachments,
    documentDownloadError
  } = useSelector(state => state.bidSummary);

  useEffect(
    () => dispatch(fetchBidSummaryTabDetails(bidId, role)),
    [dispatch, bidId, role]
  );

  const fetchTeam = () => dispatch(fetchBidTeam(bidId));
  const fetchMessages = () => dispatch(fetchBidMessages(bidId));
  const fetchAttachments = () => dispatch(fetchBidAttachments(bidId));
  const fetchShipperHistory = () => dispatch(fetchBidShipperHistory(bidId));
  const fetchAssignments = () => dispatch(fetchTLLaneCountByRegion(bidId));
  const hasTLMode = summary
    ? summary.modes.some(mode => mode.mode === modeTypes.tl)
    : false;
  const showAssignmentsPanel =
    hasTLMode &&
    (role === userRole.tlPricingAnalyst ||
      role === userRole.assetBasedPricingUser);

  return (
    <div className="bid page-container mb-24">
      <div className="summary">
        <ProgressBar
          modes={summary ? summary.modes : []}
          loading={summaryLoading}
          error={summaryError}
        />
        <div className="grid mt-28">
          <div className="grid__cell one-whole pb-16">
            <Team
              error={teamError}
              loading={teamLoading}
              fetchDetails={fetchTeam}
              team={team}
              bidId={bidId}
            />
          </div>
          <div className="grid__cell one-half pr-8">
            <Messages
              error={messagesError}
              loading={messagesLoading}
              fetchDetails={fetchMessages}
              bidId={bidId}
              bidMessages={messages}
            />
            {showAssignmentsPanel && (
              <ShipperHistory
                error={shipperHistoryError}
                loading={shipperHistoryLoading}
                fetchDetails={fetchShipperHistory}
                shipperHistory={shipperHistory}
              />
            )}
          </div>
          <div className="grid__cell one-half pl-8">
            <Attachments
              loading={attachmentsLoading}
              attachments={attachments}
              bidId={bidId}
              fetchDetails={fetchAttachments}
              error={attachmentsError}
              documentDownloadError={documentDownloadError}
            />
            {!showAssignmentsPanel && (
              <ShipperHistory
                error={shipperHistoryError}
                loading={shipperHistoryLoading}
                fetchDetails={fetchShipperHistory}
                shipperHistory={shipperHistory}
              />
            )}
            {showAssignmentsPanel && (
              <Assignments fetchDetails={fetchAssignments} bidId={bidId} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Summary;
